// Raspi API anahtari uret dialog'u.
// Plain key sadece bir kez gosterilir — kullanici kopyalayip Raspi config'ine yazmali.

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Check, Copy, KeyRound, X } from 'lucide-react';
import { Buton, Etiket, Girdi, Rozet, toast } from '@emanetx/ui';
import { useCihazAnahtariOlustur } from './api.js';

interface UretildiCevap {
  id: string;
  cihazId: string;
  ad: string;
  plainKey: string;
  sonDort: string;
}

export function AnahtarOlusturDialog({
  acik,
  onDegisim,
  noktaId,
  noktaKod,
  noktaAd,
}: {
  acik: boolean;
  onDegisim: (v: boolean) => void;
  noktaId: string;
  noktaKod: string;
  noktaAd: string;
}) {
  const [ad, setAd] = useState('');
  const [cihazId, setCihazId] = useState('');
  const [uretildi, setUretildi] = useState<UretildiCevap | null>(null);
  const [kopyalandi, setKopyalandi] = useState(false);
  const olustur = useCihazAnahtariOlustur(noktaId);

  const kapat = () => {
    setAd('');
    setCihazId('');
    setUretildi(null);
    setKopyalandi(false);
    onDegisim(false);
  };

  const onayla = async () => {
    if (ad.trim().length < 2) {
      toast.error('Anahtar adi en az 2 karakter olmali.');
      return;
    }
    try {
      const cevap = await olustur.mutateAsync({
        ad: ad.trim(),
        ...(cihazId.trim() && { cihazId: cihazId.trim() }),
      });
      setUretildi(cevap);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const kopyala = async () => {
    if (!uretildi) return;
    try {
      await navigator.clipboard.writeText(uretildi.plainKey);
      setKopyalandi(true);
      toast.success('Anahtar kopyalandi');
      setTimeout(() => setKopyalandi(false), 2000);
    } catch {
      toast.error('Kopyalanamadi — manuel sec');
    }
  };

  return (
    <Dialog.Root open={acik} onOpenChange={(v) => (v ? onDegisim(true) : kapat())}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-fume-900 dark:text-fume-50 flex items-center gap-2 text-base font-semibold">
                <KeyRound size={16} className="text-marka-500" />
                {uretildi ? 'Anahtar Üretildi' : 'Yeni Raspi API Anahtarı'}
              </Dialog.Title>
              <Dialog.Description asChild>
                <div className="text-fume-500 dark:text-fume-400 mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                  <span className="truncate">{noktaAd}</span>
                  <Rozet ton="fume" className="font-mono">
                    {noktaKod}
                  </Rozet>
                </div>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="text-fume-400 hover:text-fume-700" aria-label="Kapat">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          {!uretildi ? (
            <>
              <div className="mt-5 space-y-3">
                <div className="space-y-1.5">
                  <Etiket htmlFor="ad">Anahtar adı (etiket)</Etiket>
                  <Girdi
                    id="ad"
                    placeholder="Örn: Piazza ana raspi"
                    value={ad}
                    onChange={(e) => setAd(e.target.value)}
                    maxLength={80}
                  />
                </div>
                <div className="space-y-1.5">
                  <Etiket htmlFor="cihazId">Cihaz ID (opsiyonel)</Etiket>
                  <Girdi
                    id="cihazId"
                    placeholder="Boş bırakırsan otomatik üretilir"
                    value={cihazId}
                    onChange={(e) => setCihazId(e.target.value)}
                    maxLength={80}
                  />
                  <p className="text-fume-500 text-xs">
                    Raspi config.yaml içindeki <code className="font-mono">cihaz_id</code>.
                    Otomatik: <code className="font-mono">CIHAZ-{noktaKod}-XXXXXX</code>.
                  </p>
                </div>
              </div>
              <div className="flex flex-col-reverse gap-2 pt-5 sm:flex-row sm:justify-end">
                <Buton stil="cizgili" onClick={kapat} disabled={olustur.isPending}>
                  Vazgeç
                </Buton>
                <Buton
                  onClick={() => void onayla()}
                  disabled={olustur.isPending || ad.trim().length < 2}
                >
                  {olustur.isPending ? 'Üretiliyor…' : 'Anahtar Üret'}
                </Buton>
              </div>
            </>
          ) : (
            <>
              <div className="mt-5 space-y-4">
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs dark:border-amber-500/30 dark:bg-amber-500/10">
                  <p className="flex items-start gap-2 text-amber-800 dark:text-amber-300">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Bu anahtar bir daha gösterilmeyecek.</strong> Şimdi kopyalayıp Raspi{' '}
                      <code className="font-mono">/etc/emanetx/config.yaml</code> dosyasına yaz.
                      Kaybedersen iptal edip yeni anahtar üretmen gerekir.
                    </span>
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Etiket>Cihaz ID</Etiket>
                  <code className="bg-fume-100 dark:bg-fume-800 block rounded px-3 py-2 font-mono text-sm">
                    {uretildi.cihazId}
                  </code>
                </div>

                <div className="space-y-1.5">
                  <Etiket>API Key (plain)</Etiket>
                  <div className="flex items-center gap-2">
                    <code className="bg-fume-100 dark:bg-fume-800 flex-1 break-all rounded px-3 py-2 font-mono text-xs">
                      {uretildi.plainKey}
                    </code>
                    <Buton
                      stil="cizgili"
                      boyut="kucuk"
                      onClick={() => void kopyala()}
                      className={kopyalandi ? 'text-emerald-600' : ''}
                    >
                      {kopyalandi ? <Check size={13} /> : <Copy size={13} />}
                      {kopyalandi ? 'Kopyalandı' : 'Kopyala'}
                    </Buton>
                  </div>
                </div>

                <div className="bg-fume-50 dark:bg-fume-800/50 rounded-md p-3 text-xs">
                  <p className="text-fume-700 dark:text-fume-300 mb-2 font-semibold">
                    Raspi config.yaml örneği:
                  </p>
                  <pre className="text-fume-600 dark:text-fume-400 overflow-x-auto font-mono text-[11px] leading-relaxed">{`api_url: wss://api.emanetx.com/cihaz.io
cihaz_id: ${uretildi.cihazId}
api_key: ${uretildi.plainKey}`}</pre>
                </div>
              </div>

              <div className="flex justify-end pt-5">
                <Buton onClick={kapat}>Tamam, kapat</Buton>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
